/**
 * StrategyHubPage.js - Phase SO-1: Strategy Hub Experience
 * 
 * Part of L7 Experiences - NOT a separate platform
 * 
 * STRATEGIC OBJECTIVES FOUNDATION
 * ===============================
 * This Strategy Hub displays:
 * - Strategic Objectives overview with CRUD
 * - Contributing Portfolios, Programmes, Projects, Milestones
 * - Benefits linked to objectives
 * - Linking interface for strategic alignment
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Target,
  TrendingUp,
  Briefcase,
  Building2,
  FolderKanban,
  DollarSign,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronRight,
  RefreshCw,
  Activity,
  BarChart3,
  PieChart,
  ArrowRight,
  Plus,
  Edit2,
  Trash2,
  Link2,
  Unlink,
  X,
  Save,
  Milestone,
  ListChecks
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { useActiveTenant } from '../hooks/useActiveTenant';
import { api } from '../api/client';
import { toast } from 'sonner';

// Valid statuses
const VALID_STATUSES = ["Draft", "Approved", "Active", "At Risk", "Achieved", "Closed"];

// =============================================================================
// OBJECTIVE CARD COMPONENT
// =============================================================================
const ObjectiveCard = ({ objective, onClick, onEdit, onDelete, isSelected }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'Achieved':
        return 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600';
      case 'Active':
        return 'bg-blue-500/10 border-blue-500/30 text-blue-600';
      case 'At Risk':
        return 'bg-amber-500/10 border-amber-500/30 text-amber-600';
      case 'Draft':
        return 'bg-slate-500/10 border-slate-500/30 text-slate-600';
      case 'Approved':
        return 'bg-indigo-500/10 border-indigo-500/30 text-indigo-600';
      case 'Closed':
        return 'bg-gray-500/10 border-gray-500/30 text-gray-600';
      default:
        return 'bg-slate-500/10 border-slate-500/30 text-slate-600';
    }
  };

  const progress = objective.target_value > 0 
    ? Math.round((objective.current_value / objective.target_value) * 100)
    : 0;

  return (
    <Card 
      className={`cursor-pointer hover:shadow-md transition-all border-2 ${
        isSelected ? 'border-blue-500 shadow-md' : 'border-transparent hover:border-blue-200'
      }`}
      onClick={() => onClick(objective)}
      data-testid={`objective-card-${objective.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="font-semibold text-slate-800 line-clamp-2">{objective.title}</h3>
            <Badge className={`mt-2 ${getStatusColor(objective.status)}`}>
              {objective.status}
            </Badge>
          </div>
          <div className="flex gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onEdit(objective)}
              data-testid={`edit-objective-${objective.id}`}
            >
              <Edit2 className="h-4 w-4 text-slate-500" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onDelete(objective)}
              data-testid={`delete-objective-${objective.id}`}
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </div>

        {objective.description && (
          <p className="text-sm text-slate-500 mb-3 line-clamp-2">{objective.description}</p>
        )}

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Progress</span>
            <span className="font-medium">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {objective.owner && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-xs text-slate-400">Owner: {objective.owner}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// =============================================================================
// CREATE/EDIT OBJECTIVE MODAL
// =============================================================================
const ObjectiveModal = ({ isOpen, onClose, objective, tenantId, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    owner: '',
    executive_sponsor: '',
    status: 'Draft',
    target_date: '',
    strategic_measure_name: '',
    baseline_value: 0,
    target_value: 100,
    current_value: 0
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (objective) {
      setFormData({
        title: objective.title || '',
        description: objective.description || '',
        owner: objective.owner || '',
        executive_sponsor: objective.executive_sponsor || '',
        status: objective.status || 'Draft',
        target_date: objective.target_date ? objective.target_date.split('T')[0] : '',
        strategic_measure_name: objective.strategic_measure_name || '',
        baseline_value: objective.baseline_value || 0,
        target_value: objective.target_value || 100,
        current_value: objective.current_value || 0
      });
    } else {
      setFormData({
        title: '',
        description: '',
        owner: '',
        executive_sponsor: '',
        status: 'Draft',
        target_date: '',
        strategic_measure_name: '',
        baseline_value: 0,
        target_value: 100,
        current_value: 0
      });
    }
  }, [objective, isOpen]);

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...formData, tenant_id: tenantId };
      
      if (objective?.id) {
        await api.put(`/api/strategic-objectives/${objective.id}`, payload);
        toast.success('Objective updated successfully');
      } else {
        await api.post('/api/strategic-objectives', payload);
        toast.success('Objective created successfully');
      }
      
      onSave();
      onClose();
    } catch (error) {
      console.error('Failed to save objective:', error);
      toast.error(error.response?.data?.detail || 'Failed to save objective');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-blue-600" />
            {objective ? 'Edit Strategic Objective' : 'Create Strategic Objective'}
          </DialogTitle>
          <DialogDescription>
            Define a strategic objective to track organisational goals and align initiatives.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Digital Transformation Excellence"
              data-testid="objective-title-input"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the strategic objective and its expected outcomes..."
              rows={3}
              data-testid="objective-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="owner">Owner</Label>
              <Input
                id="owner"
                value={formData.owner}
                onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                placeholder="e.g., John Smith"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="sponsor">Executive Sponsor</Label>
              <Input
                id="sponsor"
                value={formData.executive_sponsor}
                onChange={(e) => setFormData({ ...formData, executive_sponsor: e.target.value })}
                placeholder="e.g., Jane Doe"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger data-testid="objective-status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {VALID_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="target_date">Target Date</Label>
              <Input
                id="target_date"
                type="date"
                value={formData.target_date}
                onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
              />
            </div>
          </div>

          <div className="border-t pt-4 mt-2">
            <h4 className="font-medium text-sm text-slate-700 mb-3">Strategic Measure</h4>
            <div className="grid gap-2 mb-4">
              <Label htmlFor="measure_name">Measure Name</Label>
              <Input
                id="measure_name"
                value={formData.strategic_measure_name}
                onChange={(e) => setFormData({ ...formData, strategic_measure_name: e.target.value })}
                placeholder="e.g., Digital Maturity Score, Customer Satisfaction Index"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="baseline">Baseline Value</Label>
                <Input
                  id="baseline"
                  type="number"
                  value={formData.baseline_value}
                  onChange={(e) => setFormData({ ...formData, baseline_value: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="current">Current Value</Label>
                <Input
                  id="current"
                  type="number"
                  value={formData.current_value}
                  onChange={(e) => setFormData({ ...formData, current_value: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="target">Target Value</Label>
                <Input
                  id="target"
                  type="number"
                  value={formData.target_value}
                  onChange={(e) => setFormData({ ...formData, target_value: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="save-objective-btn">
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {objective ? 'Update' : 'Create'} Objective
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// LINKING MODAL
// =============================================================================
const LinkingModal = ({ isOpen, onClose, objective, tenantId, onUpdate }) => {
  const [linkedEntities, setLinkedEntities] = useState(null);
  const [availableEntities, setAvailableEntities] = useState({
    portfolios: [],
    programs: [],
    projects: [],
    milestones: []
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('programs');

  const fetchLinkedEntities = useCallback(async () => {
    if (!objective?.id || !tenantId) return;
    
    try {
      const response = await api.get(`/api/strategic-objectives/${objective.id}/linked-entities?tenant_id=${tenantId}`);
      setLinkedEntities(response.data?.linked || {});
    } catch (error) {
      console.error('Failed to fetch linked entities:', error);
    }
  }, [objective?.id, tenantId]);

  const fetchAvailableEntities = useCallback(async () => {
    if (!tenantId) return;
    
    try {
      setLoading(true);
      // Use endpoints that exist and work
      const [portfoliosRes, programsRes, projectsRes] = await Promise.all([
        api.get(`/api/portfolios?tenant_id=${tenantId}`).catch(() => ({ data: [] })),
        api.get(`/api/programs`).catch(() => ({ data: { programs: [] } })),
        api.get(`/api/projects?tenant_id=${tenantId}`).catch(() => ({ data: { projects: [] } }))
      ]);
      
      // Get milestones from PM endpoint
      let milestones = [];
      try {
        const milestonesRes = await api.get(`/api/milestones/pm?tenant_id=${tenantId}`);
        milestones = milestonesRes.data?.milestones || [];
      } catch (e) {
        console.warn('Could not fetch milestones:', e);
      }
      
      // Normalize the data
      const normalizedPortfolios = portfoliosRes.data?.portfolios || portfoliosRes.data || [];
      const normalizedPrograms = (programsRes.data?.programs || []).map(p => ({
        id: p.program_id || p.id,
        name: p.name,
        status: p.status || 'Active'
      }));
      const normalizedProjects = (projectsRes.data?.projects || []).map(p => ({
        id: p.id,
        name: p.name,
        status: p.status || p.health || 'Active'
      }));
      const normalizedMilestones = milestones.map(m => ({
        id: m.id || m._id,
        name: m.name || m.title,
        status: m.status || 'Active',
        due_date: m.due_date || m.end_date
      }));
      
      setAvailableEntities({
        portfolios: normalizedPortfolios,
        programs: normalizedPrograms,
        projects: normalizedProjects,
        milestones: normalizedMilestones
      });
    } catch (error) {
      console.error('Failed to fetch available entities:', error);
      toast.error('Failed to load available entities');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (isOpen && objective) {
      fetchLinkedEntities();
      fetchAvailableEntities();
    }
  }, [isOpen, objective, fetchLinkedEntities, fetchAvailableEntities]);

  const handleLink = async (entityType, entityId) => {
    try {
      const endpointMap = {
        portfolios: 'link-portfolio',
        programs: 'link-programme',
        projects: 'link-project',
        milestones: 'link-milestone'
      };
      const idFieldMap = {
        portfolios: 'portfolio_id',
        programs: 'program_id',
        projects: 'project_id',
        milestones: 'milestone_id'
      };
      
      await api.post(`/api/strategic-objectives/${objective.id}/${endpointMap[entityType]}`, {
        [idFieldMap[entityType]]: entityId,
        tenant_id: tenantId
      });
      
      toast.success(`${entityType.slice(0, -1)} linked successfully`);
      fetchLinkedEntities();
      onUpdate();
    } catch (error) {
      console.error('Failed to link entity:', error);
      toast.error('Failed to link entity');
    }
  };

  const handleUnlink = async (entityType, entityId) => {
    try {
      const endpointMap = {
        portfolios: 'unlink-portfolio',
        programs: 'unlink-programme',
        projects: 'unlink-project',
        milestones: 'unlink-milestone'
      };
      const idFieldMap = {
        portfolios: 'portfolio_id',
        programs: 'program_id',
        projects: 'project_id',
        milestones: 'milestone_id'
      };
      
      await api.post(`/api/strategic-objectives/${objective.id}/${endpointMap[entityType]}`, {
        [idFieldMap[entityType]]: entityId,
        tenant_id: tenantId
      });
      
      toast.success(`${entityType.slice(0, -1)} unlinked successfully`);
      fetchLinkedEntities();
      onUpdate();
    } catch (error) {
      console.error('Failed to unlink entity:', error);
      toast.error('Failed to unlink entity');
    }
  };

  const isLinked = (entityType, entityId) => {
    return linkedEntities?.[entityType]?.some(e => e.id === entityId);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'portfolios': return <Briefcase className="h-4 w-4" />;
      case 'programs': return <Building2 className="h-4 w-4" />;
      case 'projects': return <FolderKanban className="h-4 w-4" />;
      case 'milestones': return <Milestone className="h-4 w-4" />;
      default: return <ListChecks className="h-4 w-4" />;
    }
  };

  const renderEntityList = (entityType) => {
    const entities = availableEntities[entityType] || [];
    const linked = linkedEntities?.[entityType] || [];
    
    return (
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {/* Linked entities first */}
        {linked.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-green-700 mb-2 flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              Linked ({linked.length})
            </h4>
            {linked.map((entity) => (
              <div 
                key={entity.id} 
                className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg mb-2"
              >
                <div className="flex items-center gap-2">
                  {getIcon(entityType)}
                  <span className="font-medium">{entity.name || entity.title}</span>
                  <Badge variant="outline" className="text-xs">{entity.status}</Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleUnlink(entityType, entity.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Unlink className="h-4 w-4 mr-1" />
                  Unlink
                </Button>
              </div>
            ))}
          </div>
        )}
        
        {/* Available to link */}
        <h4 className="text-sm font-medium text-slate-700 mb-2">
          Available to Link ({entities.filter(e => !isLinked(entityType, e.id)).length})
        </h4>
        {entities.filter(e => !isLinked(entityType, e.id)).map((entity) => (
          <div 
            key={entity.id} 
            className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg"
          >
            <div className="flex items-center gap-2">
              {getIcon(entityType)}
              <span>{entity.name || entity.title}</span>
              {entity.status && <Badge variant="outline" className="text-xs">{entity.status}</Badge>}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleLink(entityType, entity.id)}
              className="text-blue-600 hover:text-blue-700"
            >
              <Link2 className="h-4 w-4 mr-1" />
              Link
            </Button>
          </div>
        ))}
        
        {entities.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-4">
            No {entityType} available
          </p>
        )}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-blue-600" />
            Link Entities to: {objective?.title}
          </DialogTitle>
          <DialogDescription>
            Connect portfolios, programmes, projects, and milestones to this strategic objective.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden">
            <TabsList className="grid grid-cols-4 mb-4">
              <TabsTrigger value="programs" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Programmes
                {linkedEntities?.programs?.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{linkedEntities.programs.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="projects" className="flex items-center gap-2">
                <FolderKanban className="h-4 w-4" />
                Projects
                {linkedEntities?.projects?.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{linkedEntities.projects.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="milestones" className="flex items-center gap-2">
                <Milestone className="h-4 w-4" />
                Milestones
                {linkedEntities?.milestones?.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{linkedEntities.milestones.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="portfolios" className="flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Portfolios
                {linkedEntities?.portfolios?.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{linkedEntities.portfolios.length}</Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <div className="overflow-y-auto flex-1">
              <TabsContent value="programs" className="mt-0">
                {renderEntityList('programs')}
              </TabsContent>
              <TabsContent value="projects" className="mt-0">
                {renderEntityList('projects')}
              </TabsContent>
              <TabsContent value="milestones" className="mt-0">
                {renderEntityList('milestones')}
              </TabsContent>
              <TabsContent value="portfolios" className="mt-0">
                {renderEntityList('portfolios')}
              </TabsContent>
            </div>
          </Tabs>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// SUMMARY METRICS
// =============================================================================
const SummaryMetrics = ({ summary }) => {
  const metrics = [
    { label: 'Total Objectives', value: summary.total_objectives || 0, icon: Target, color: 'blue' },
    { label: 'Active', value: summary.active || 0, icon: Activity, color: 'emerald' },
    { label: 'At Risk', value: summary.at_risk || 0, icon: AlertTriangle, color: 'amber' },
    { label: 'Achieved', value: summary.achieved || 0, icon: CheckCircle, color: 'green' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {metrics.map((metric) => (
        <Card key={metric.label} className="border-l-4" style={{ borderLeftColor: `var(--${metric.color}-500)` }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{metric.label}</p>
                <p className="text-2xl font-bold text-slate-800">{metric.value}</p>
              </div>
              <metric.icon className={`h-8 w-8 text-${metric.color}-500 opacity-50`} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// =============================================================================
// OBJECTIVE DETAIL PANEL
// =============================================================================
const ObjectiveDetailPanel = ({ objective, hubData, loading, onLink }) => {
  if (loading) {
    return (
      <Card className="xl:col-span-2">
        <CardContent className="p-8 flex items-center justify-center min-h-[400px]">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        </CardContent>
      </Card>
    );
  }

  if (!hubData) {
    return (
      <Card className="xl:col-span-2">
        <CardContent className="p-8 text-center">
          <Target className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Select an objective to view details</p>
        </CardContent>
      </Card>
    );
  }

  const progress = hubData.objective?.target_value > 0 
    ? Math.round((hubData.objective?.current_value / hubData.objective?.target_value) * 100)
    : 0;

  return (
    <Card className="xl:col-span-2">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl">{hubData.objective?.title}</CardTitle>
            <CardDescription className="mt-1">{hubData.objective?.description}</CardDescription>
          </div>
          <Button onClick={onLink} variant="outline" data-testid="link-entities-btn">
            <Link2 className="h-4 w-4 mr-2" />
            Link Entities
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Progress */}
        <div className="bg-slate-50 rounded-lg p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium">Progress toward Target</span>
            <span className="text-2xl font-bold text-blue-600">{progress}%</span>
          </div>
          <Progress value={progress} className="h-3" />
          <div className="flex justify-between text-sm text-slate-500 mt-2">
            <span>Baseline: {hubData.objective?.baseline_value || 0}</span>
            <span>Current: {hubData.objective?.current_value || 0}</span>
            <span>Target: {hubData.objective?.target_value || 0}</span>
          </div>
        </div>

        {/* Linked Entities Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <Briefcase className="h-6 w-6 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-600">{hubData.portfolios?.length || 0}</p>
            <p className="text-sm text-slate-600">Portfolios</p>
          </div>
          <div className="bg-indigo-50 rounded-lg p-4 text-center">
            <Building2 className="h-6 w-6 text-indigo-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-indigo-600">{hubData.programs?.length || 0}</p>
            <p className="text-sm text-slate-600">Programmes</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <FolderKanban className="h-6 w-6 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-purple-600">{hubData.projects?.length || 0}</p>
            <p className="text-sm text-slate-600">Projects</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-4 text-center">
            <DollarSign className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-emerald-600">{hubData.benefits?.length || 0}</p>
            <p className="text-sm text-slate-600">Benefits</p>
          </div>
        </div>

        {/* Contributing Programmes */}
        {hubData.programs?.length > 0 && (
          <div>
            <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Contributing Programmes
            </h4>
            <div className="grid gap-2">
              {hubData.programs.slice(0, 5).map((prog) => (
                <div key={prog.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span>{prog.name}</span>
                  <Badge variant="outline">{prog.status || prog.health || 'Active'}</Badge>
                </div>
              ))}
              {hubData.programs.length > 5 && (
                <p className="text-sm text-slate-500 text-center">
                  + {hubData.programs.length - 5} more programmes
                </p>
              )}
            </div>
          </div>
        )}

        {/* Benefits */}
        {hubData.benefits?.length > 0 && (
          <div>
            <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Linked Benefits
            </h4>
            <div className="grid gap-2">
              {hubData.benefits.slice(0, 5).map((benefit) => (
                <div key={benefit.id} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                  <span>{benefit.title}</span>
                  <span className="font-medium text-emerald-600">
                    £{((benefit.expected_value || 0) / 1000).toFixed(0)}K expected
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metadata */}
        <div className="border-t pt-4 grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-slate-500">Owner:</span>
            <span className="ml-2 font-medium">{hubData.objective?.owner || 'Not assigned'}</span>
          </div>
          <div>
            <span className="text-slate-500">Executive Sponsor:</span>
            <span className="ml-2 font-medium">{hubData.objective?.executive_sponsor || 'Not assigned'}</span>
          </div>
          <div>
            <span className="text-slate-500">Target Date:</span>
            <span className="ml-2 font-medium">
              {hubData.objective?.target_date 
                ? new Date(hubData.objective.target_date).toLocaleDateString() 
                : 'Not set'}
            </span>
          </div>
          <div>
            <span className="text-slate-500">Strategic Measure:</span>
            <span className="ml-2 font-medium">{hubData.objective?.strategic_measure_name || 'Not defined'}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const StrategyHubPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tenantId, loading: tenantLoading } = useActiveTenant();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overviewData, setOverviewData] = useState(null);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [selectedHubData, setSelectedHubData] = useState(null);
  const [hubLoading, setHubLoading] = useState(false);

  // Modal states
  const [objectiveModalOpen, setObjectiveModalOpen] = useState(false);
  const [editingObjective, setEditingObjective] = useState(null);
  const [linkingModalOpen, setLinkingModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [objectiveToDelete, setObjectiveToDelete] = useState(null);

  // Fetch overview data
  const fetchOverview = useCallback(async () => {
    if (!tenantId) return;

    try {
      setLoading(true);
      const response = await api.get(`/api/strategic-objectives/hub/overview?tenant_id=${tenantId}`);
      setOverviewData(response.data?.overview || null);
    } catch (error) {
      console.error('Failed to fetch strategy hub overview:', error);
      toast.error('Failed to load strategy overview');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Fetch single objective hub data
  const fetchObjectiveHub = useCallback(async (objectiveId) => {
    if (!tenantId || !objectiveId) return;

    try {
      setHubLoading(true);
      const response = await api.get(`/api/strategic-objectives/${objectiveId}/hub?tenant_id=${tenantId}`);
      setSelectedHubData(response.data?.hub || null);
    } catch (error) {
      console.error('Failed to fetch objective hub data:', error);
      toast.error('Failed to load objective details');
    } finally {
      setHubLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (tenantId) {
      fetchOverview();
    }
  }, [tenantId, fetchOverview]);

  // Handle objective selection
  const handleObjectiveClick = async (objective) => {
    setSelectedObjective(objective);
    await fetchObjectiveHub(objective.id);
  };

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchOverview();
    if (selectedObjective) {
      await fetchObjectiveHub(selectedObjective.id);
    }
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedObjective(null);
    setSelectedHubData(null);
  };

  // Create new objective
  const handleCreate = () => {
    setEditingObjective(null);
    setObjectiveModalOpen(true);
  };

  // Edit objective
  const handleEdit = (objective) => {
    setEditingObjective(objective);
    setObjectiveModalOpen(true);
  };

  // Delete objective
  const handleDeleteClick = (objective) => {
    setObjectiveToDelete(objective);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!objectiveToDelete) return;

    try {
      await api.delete(`/api/strategic-objectives/${objectiveToDelete.id}?tenant_id=${tenantId}`);
      toast.success('Objective deleted successfully');
      setDeleteConfirmOpen(false);
      setObjectiveToDelete(null);
      if (selectedObjective?.id === objectiveToDelete.id) {
        handleClearSelection();
      }
      fetchOverview();
    } catch (error) {
      console.error('Failed to delete objective:', error);
      toast.error('Failed to delete objective');
    }
  };

  // Open linking modal
  const handleOpenLinking = () => {
    setLinkingModalOpen(true);
  };

  // After save
  const handleSaveComplete = () => {
    fetchOverview();
    if (selectedObjective) {
      fetchObjectiveHub(selectedObjective.id);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
          <p className="text-slate-500">Loading Strategy Hub...</p>
        </div>
      </div>
    );
  }

  const objectives = overviewData?.objectives || [];
  const summary = overviewData?.summary || {};

  return (
    <div className="p-6 max-w-[1800px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Target className="h-7 w-7 text-blue-600" />
            Strategy Hub
          </h1>
          <p className="text-slate-500 mt-1">
            Strategic Objectives and their contribution to organisational outcomes
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={handleCreate}
            data-testid="create-objective-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Objective
          </Button>
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-strategy-hub"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Metrics */}
      <SummaryMetrics summary={summary} />

      {/* Main Content */}
      {objectives.length === 0 ? (
        <Card className="p-12 text-center">
          <Target className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No Strategic Objectives</h3>
          <p className="text-slate-500 max-w-md mx-auto mb-6">
            Strategic objectives have not been defined yet. Create objectives to track 
            organisational goals and their contribution to portfolios and programmes.
          </p>
          <Button onClick={handleCreate} data-testid="create-first-objective-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create First Objective
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Objectives List */}
          <div className={`${selectedObjective ? 'xl:col-span-1' : 'xl:col-span-3'}`}>
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="h-5 w-5 text-blue-500" />
                    Strategic Objectives
                  </CardTitle>
                  <Badge variant="outline">{objectives.length} objectives</Badge>
                </div>
                {selectedObjective && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleClearSelection}
                    className="mt-2"
                  >
                    ← Show all objectives
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className={`grid gap-4 ${selectedObjective ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                  {objectives.map((objective) => (
                    <ObjectiveCard
                      key={objective.id}
                      objective={objective}
                      onClick={handleObjectiveClick}
                      onEdit={handleEdit}
                      onDelete={handleDeleteClick}
                      isSelected={selectedObjective?.id === objective.id}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detail Panel */}
          {selectedObjective && (
            <ObjectiveDetailPanel
              objective={selectedObjective}
              hubData={selectedHubData}
              loading={hubLoading}
              onLink={handleOpenLinking}
            />
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <ObjectiveModal
        isOpen={objectiveModalOpen}
        onClose={() => setObjectiveModalOpen(false)}
        objective={editingObjective}
        tenantId={tenantId}
        onSave={handleSaveComplete}
      />

      {/* Linking Modal */}
      <LinkingModal
        isOpen={linkingModalOpen}
        onClose={() => setLinkingModalOpen(false)}
        objective={selectedObjective}
        tenantId={tenantId}
        onUpdate={handleSaveComplete}
      />

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Strategic Objective
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{objectiveToDelete?.title}&quot;? 
              This will also remove all links to portfolios, programmes, and benefits.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} data-testid="confirm-delete-btn">
              Delete Objective
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StrategyHubPage;
